import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, Empty, List, Space, Spin, Tag, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getEmployeeSafety, getMeta } from "./api";
import { QualificationDialog } from "./Qualifications";
import { LIGHT_COLORS, formatDay, qualificationLabels, stateLabels } from "./format";
import "./safety.css";

/** "Safety" tab of an employee's profile: due Unterweisungen, qualifications, attended sessions. */
const EmployeeSafety = ({ employeeId }) => {
  const { t } = useTranslation("dashboard");
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [denied, setDenied] = useState(false);
  const [adding, setAdding] = useState(false);
  const labels = stateLabels(t);
  const types = qualificationLabels(t);

  const load = () =>
    Promise.all([getEmployeeSafety(employeeId), getMeta()])
      .then(([res, metaRes]) => {
        setData(res.data);
        setMeta(metaRes.data);
      })
      .catch(error => {
        if (error?.response?.status === 403) setDenied(true);
      });

  useEffect(() => {
    if (employeeId) load();
  }, [employeeId]);

  if (denied) {
    return <Empty description={t("You are not allowed to see occupational safety records.")} />;
  }
  if (!data) {
    return (
      <div className="safety-page sf-center">
        <Spin />
      </div>
    );
  }

  const tag = row => <Tag color={LIGHT_COLORS[row.light]}>{labels[row.state]}</Tag>;

  return (
    <div className="safety-page">
      <Card size="small" title={t("Safety instructions")} className="mb-3">
        <List
          dataSource={data.due}
          locale={{ emptyText: t("No instruction topics apply") }}
          renderItem={row => (
            <List.Item extra={tag(row)}>
              <List.Item.Meta
                title={<span className="sf-wrap">{row.topicTitle}</span>}
                description={
                  row.lastDate
                    ? t("Last {{last}}, next by {{next}}", {
                        last: formatDay(row.lastDate),
                        next: formatDay(row.dueDate)
                      })
                    : t("Never instructed")
                }
              />
            </List.Item>
          )}
        />
      </Card>

      <Card
        size="small"
        title={t("Qualifications & checks")}
        className="mb-3"
        extra={
          meta?.can.create && (
            <Button size="small" icon={<PlusOutlined />} onClick={() => setAdding(true)}>
              {t("Add")}
            </Button>
          )
        }
      >
        <List
          dataSource={data.qualifications}
          locale={{ emptyText: t("No qualifications recorded") }}
          renderItem={row => (
            <List.Item extra={row.state === "none" ? null : tag(row)}>
              <List.Item.Meta
                title={
                  <span className="sf-wrap">
                    {[types[row.type], row.title].filter(Boolean).join(": ")}
                  </span>
                }
                description={
                  <Space size={4} wrap>
                    <span>{formatDay(row.issuedOn)}</span>
                    {row.validUntil && (
                      <span>
                        · {t("valid until {{date}}", { date: formatDay(row.validUntil) })}
                      </span>
                    )}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      <Card size="small" title={t("Attended instructions")}>
        <List
          dataSource={data.sessions}
          locale={{ emptyText: t("No safety instructions yet") }}
          renderItem={s => (
            <List.Item
              extra={
                s.signedAt ? (
                  <Tag color="success">{t("Signed")}</Tag>
                ) : s.absent ? (
                  <Tag>{t("Absent")}</Tag>
                ) : (
                  <Tag color="processing">{t("Open")}</Tag>
                )
              }
            >
              <List.Item.Meta
                title={
                  <Link to={`/user/safety/sessions/${s._id}`}>
                    {s.number} · {formatDay(s.date)}
                  </Link>
                }
                description={
                  <Typography.Text className="sf-wrap">{s.topics.join(", ")}</Typography.Text>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      <QualificationDialog
        open={adding}
        meta={meta}
        employeeId={employeeId}
        onClose={() => setAdding(false)}
        onSaved={() => {
          setAdding(false);
          load();
        }}
      />
    </div>
  );
};

export default EmployeeSafety;
